/* OS Listicle - Offer Card */
(() => {
  const SECTION_SELECTOR = '.os-listicle-offer-card';
  const CTA_SELECTOR = '[data-os-listicle-offer-cta]';
  const TIMER_SELECTOR = '[data-os-listicle-offer-timer]';

  function normalizeDiscountCodes(rawCodes) {
    return String(rawCodes || '')
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean);
  }

  function getSectionConfig(section) {
    if (!section) {
      return {
        bogoUrlParamsEnabled: false,
        bogoUrlParams: '',
        checkoutUrl: '/checkout',
        giftCardAttrSubEnabled: false,
        giftCardAttrOtpEnabled: false,
        giftCardAmount: '',
      };
    }

    return {
      bogoUrlParamsEnabled: section.dataset.bogoUrlParamsEnabled === 'true',
      bogoUrlParams: section.dataset.bogoUrlParams || '',
      checkoutUrl: section.dataset.checkoutUrl || '/checkout',
      giftCardAttrSubEnabled: section.dataset.giftCardAttrSubEnabled === 'true',
      giftCardAttrOtpEnabled: section.dataset.giftCardAttrOtpEnabled === 'true',
      giftCardAmount: section.dataset.giftCardAttrAmount || '',
    };
  }

  function getPromoGiftItems(section) {
    if (!section || section.dataset.customPromoEnabled !== 'true') return [];
    const landingSource = (section.dataset.customPromoLandingSource || '').trim();
    const rawIds = (section.dataset.customPromoGiftIds || '').trim();
    if (!landingSource || !rawIds) return [];
    return rawIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((id) => ({
        id: Number(id),
        quantity: 1,
        properties: {
          _landing_source: landingSource,
          __landing_source: landingSource,
          _promo_gift: 'true',
        },
      }));
  }

  function attachPromoLandingSource(section, item) {
    if (!section || section.dataset.customPromoEnabled !== 'true') return item;
    const landingSource = (section.dataset.customPromoLandingSource || '').trim();
    if (!landingSource) return item;
    return {
      ...item,
      properties: {
        ...(item.properties || {}),
        _landing_source: landingSource,
        __landing_source: landingSource,
      },
    };
  }

  function normalizeBogoParamString(rawValue) {
    const bogo = window.AmbrosiaBogoUrlParams;
    if (bogo && typeof bogo.normalize === 'function') {
      return bogo.normalize(rawValue);
    }

    if (!rawValue) return '';
    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.indexOf('://') !== -1) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function applyBogoUrlParams(section, options) {
    const bogo = window.AmbrosiaBogoUrlParams;
    if (!bogo) return false;
    return bogo.apply(getSectionConfig(section), options);
  }

  function extractBogoCartAttributes(section) {
    const sectionConfig = getSectionConfig(section);
    const raw = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!raw) return null;

    const attrs = {};
    let hasAny = false;

    new URLSearchParams(raw).forEach((value, key) => {
      if (!key) return;
      attrs[key] = value;
      hasAny = true;
    });

    return hasAny ? attrs : null;
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      function check() {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            if (cart.item_count > knownItemCount) {
              resolve(true);
              return;
            }

            elapsed += interval;
            if (elapsed >= maxWait) {
              resolve(false);
              return;
            }

            setTimeout(check, interval);
          })
          .catch(() => resolve(false));
      }

      setTimeout(check, interval);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, section) {
    const sectionConfig = getSectionConfig(section);
    if (!sectionConfig.bogoUrlParamsEnabled) return;

    const sanitizedParams = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!sanitizedParams) return;

    const incomingParams = new URLSearchParams(sanitizedParams);
    incomingParams.forEach((value, key) => {
      if (!key) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getCheckoutUrlWithCurrentParams(section, discountCodes) {
    const sectionConfig = getSectionConfig(section);
    const checkoutBase = sectionConfig.checkoutUrl || '/checkout';
    const checkoutUrl = new URL(checkoutBase, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);
    const bogo = window.AmbrosiaBogoUrlParams;

    if (bogo && typeof bogo.copyPublicSearchParams === 'function') {
      bogo.copyPublicSearchParams(currentParams, checkoutUrl);
    } else {
      currentParams.forEach((value, key) => {
        if (!key || key === '_amb_bogo_reload') return;
        checkoutUrl.searchParams.set(key, value);
      });
    }

    mergeConfiguredBogoParams(checkoutUrl, section);

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }

    return checkoutUrl.toString();
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, options);

    if (!response.ok) {
      let message = 'Something went wrong. Please try again.';

      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {}

      throw new Error(message);
    }

    return response.json();
  }

  async function clearCart() {
    await fetchJson('/cart/clear.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });
  }

  function getSelectedVariantId(button) {
    const block = button.closest('.os-listicle-offer-card__block--cta');
    const picker = block ? block.querySelector('[data-os-listicle-offer-variant-picker]') : null;

    if (picker && picker.value) {
      return picker.value;
    }

    return button.getAttribute('data-variant-id');
  }

  async function addMainItem(button) {
    const variantId = getSelectedVariantId(button);
    const sellingPlanId = button.getAttribute('data-selling-plan-id');
    const quantity = parseInt(button.getAttribute('data-quantity') || '1', 10);

    if (!variantId) {
      throw new Error('Missing product or variant.');
    }

    const item = {
      id: Number(variantId),
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };

    if (sellingPlanId) {
      item.selling_plan = Number(sellingPlanId);
    }

    return fetchJson('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [item],
      }),
    });
  }

  function redirectToCheckout(button, section) {
    const discountCodes = normalizeDiscountCodes(button.getAttribute('data-discount-codes'));
    applyBogoUrlParams(section);
    window.location.href = getCheckoutUrlWithCurrentParams(section, discountCodes);
  }

  function redirectToAssignedLink(button) {
    const linkUrl = button.getAttribute('data-link-url');
    const openNewTab = button.getAttribute('data-link-new-tab') === '1';

    if (!linkUrl) {
      showError(button, 'Missing assigned link URL.');
      return;
    }

    if (openNewTab) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.href = linkUrl;
  }

  function showError(button, message) {
    const block = button.closest('.os-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-os-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideError(button) {
    const block = button.closest('.os-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-os-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  async function handleCtaClick(button, section) {
    if (!button || button.classList.contains('is-loading')) return;

    const ctaBehavior = button.getAttribute('data-cta-behavior') || 'checkout';
    const originalText = button.textContent;
    const loadingText = button.getAttribute('data-loading-text') || 'ADDING...';
    const shouldClearCart = button.getAttribute('data-clear-cart') === '1';

    hideError(button);

    if (ctaBehavior === 'link') {
      redirectToAssignedLink(button);
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;
    button.textContent = loadingText;

    try {
      if (!window.AmbrosiaFastCheckout) {
        throw new Error('Checkout helper failed to load. Please refresh and try again.');
      }

      const variantId = getSelectedVariantId(button);
      const sellingPlanId = button.getAttribute('data-selling-plan-id');
      const quantity = parseInt(button.getAttribute('data-quantity') || '1', 10);

      if (!variantId) {
        throw new Error('Missing product or variant.');
      }

      let item = {
        id: Number(variantId),
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      };

      if (sellingPlanId) {
        item.selling_plan = Number(sellingPlanId);
      }

      item = attachPromoLandingSource(section, item);

      const items = [item];
      getPromoGiftItems(section).forEach((gift) => items.push(gift));

      const sectionConfig = getSectionConfig(section);
      await window.AmbrosiaFastCheckout.addThenCheckout({
        sectionConfig: sectionConfig,
        clearCart: shouldClearCart,
        items: items,
        waitForGift: sectionConfig.bogoUrlParamsEnabled,
        isSubscription: Boolean(sellingPlanId),
        discountCodes: normalizeDiscountCodes(button.getAttribute('data-discount-codes')),
      });
    } catch (error) {
      showError(button, error.message || 'Something went wrong. Please try again.');

      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = originalText;
      if (window.AmbrosiaFastCheckout) window.AmbrosiaFastCheckout.endRedirecting();
    }
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function initTimer(timer) {
    if (!timer || timer.__osListicleOfferTimerBound) return;
    timer.__osListicleOfferTimerBound = true;

    let durationMinutes = parseInt(timer.getAttribute('data-duration-minutes'), 10);
    const storageKey = timer.getAttribute('data-storage-key');

    const hoursEl = timer.querySelector('[data-os-listicle-offer-hours]');
    const minsEl = timer.querySelector('[data-os-listicle-offer-mins]');
    const secsEl = timer.querySelector('[data-os-listicle-offer-secs]');

    if (!durationMinutes || durationMinutes < 1) {
      durationMinutes = 1;
    }

    let endTime = null;
    const now = Date.now();

    try {
      endTime = parseInt(window.localStorage.getItem(storageKey), 10);
    } catch (error) {
      endTime = null;
    }

    if (!endTime || endTime <= now) {
      endTime = now + durationMinutes * 60 * 1000;

      try {
        window.localStorage.setItem(storageKey, String(endTime));
      } catch (error) {}
    }

    function renderTimer() {
      let remaining = endTime - Date.now();

      if (remaining <= 0) {
        remaining = 0;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(minutes);
      if (secsEl) secsEl.textContent = pad(seconds);

      if (remaining > 0) {
        window.setTimeout(renderTimer, 1000);
      }
    }

    renderTimer();
  }

  function initSection(section) {
    if (!section || section.__osListicleOfferBound) return;
    section.__osListicleOfferBound = true;

    if (applyBogoUrlParams(section, { allowReload: true })) return;

    section.querySelectorAll(TIMER_SELECTOR).forEach(initTimer);

    section.addEventListener('change', (event) => {
      const picker = event.target.closest('[data-os-listicle-offer-variant-picker]');
      if (!picker || !section.contains(picker)) return;

      const block = picker.closest('.os-listicle-offer-card__block--cta');
      const button = block ? block.querySelector(CTA_SELECTOR) : null;

      if (button && picker.value) {
        button.setAttribute('data-variant-id', picker.value);
      }
    });

    section.addEventListener('click', (event) => {
      const button = event.target.closest(CTA_SELECTOR);
      if (!button || !section.contains(button)) return;

      event.preventDefault();
      handleCtaClick(button, section);
    });
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState !== 'loading') {
    init();
  }
})();
